import React, { useState } from 'react';
import FilterBar from '../../components/filters/FilterBar';
import DashboardBento from '../../components/dashboard/DashboardBento';
import PageLayout from '../../components/layout/PageLayout';
import ErrorBoundary from '../../components/ui/ErrorBoundary';
import { useFilterPersistence } from '../../hooks/useFilterPersistence';
import { useAutoRefresh } from '../../hooks/useAnalytics';
import { useSettings } from '../../contexts/SettingsContext';
import { useToast } from '../../components/ui/Toast';
import { PAGE_INFO, REFRESH_CONFIG, TOAST_DURATIONS, STORAGE_KEYS, FILTER_PERIODS } from '../../constants';
import './Dashboard.css';

/**
 * Dashboard Page Component
 * Main overview page displaying key metrics and statistics
 */
const Dashboard = () => {
  const { period, setPeriod, platform, setPlatform } = useFilterPersistence({
    pageKey: 'dashboard',
    defaultPeriod: FILTER_PERIODS.ALL, // Default to "All Time"
    defaultPlatform: null // Default to "All Platforms"
  });
  const [expanded, setExpanded] = useState(false);
  const { showToast } = useToast();
  const settings = useSettings();

  // Auto-refresh with toast notification - use user preference interval
  const autoRefreshEnabled = settings.autoRefresh;
  const refreshInterval = (settings.autoRefreshInterval || 30) * 1000; // Convert to milliseconds
  const { refetch: refetchAll } = useAutoRefresh(() => {
    if (autoRefreshEnabled) {
      showToast('Dashboard data refreshed', 'success', TOAST_DURATIONS.SHORT);
      // No reload needed - settings are reactive, just refetch data
    }
  }, autoRefreshEnabled ? refreshInterval : null); // Pass null to disable if off

  const handleRefresh = () => {
    showToast('Refreshing dashboard data...', 'info', TOAST_DURATIONS.SHORT);
    refetchAll();
  };

  const headerActions = (
    <div className="filter-bar-right">
      <button 
        className="refresh-button"
        onClick={handleRefresh}
        title="Refresh all data"
        aria-label="Refresh dashboard data"
      >
        <i className="bi bi-arrow-repeat icon-brand" style={{ marginRight: 6 }}></i>
        Refresh
      </button>
      <button
        className="admin-btn-secondary"
        onClick={() => setExpanded(prev => !prev)}
        title={expanded ? 'Show fewer metrics' : 'Show more metrics'}
        aria-label={expanded ? 'Collapse metrics' : 'Expand metrics'}
      >
        {expanded ? '− Show less' : '+ Show more'}
      </button>
    </div>
  );

  const filters = (
    <FilterBar
      period={period}
      onPeriodChange={setPeriod}
      platform={platform}
      onPlatformChange={setPlatform}
      rightActions={headerActions}
    />
  );

  return (
    <ErrorBoundary>
      <PageLayout
        title={PAGE_INFO.DASHBOARD.title}
        description={PAGE_INFO.DASHBOARD.description}
        filters={filters}
      >
        <div className="dashboard-content">
          <DashboardBento
            period={period}
            platform={platform}
            compact={settings.compactMode || !expanded}
          />
        </div>
      </PageLayout>
    </ErrorBoundary>
  );
};

export default Dashboard;
